import { css } from '@emotion/react';
import { color, Icon } from '@uniquegood/realworld-studio-design';
import { animate, hover } from 'motion';
import { ButtonHTMLAttributes, useEffect, useRef } from 'react';

interface AiStyleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

export function AiStyleButton({ children, ...props }: AiStyleButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hoverStartAnimation = (element: Element) =>
    animate(
      element,
      {
        scale: 1.05,
        boxShadow: '0 0 33.6px 0 rgba(255, 255, 255, 0.4)'
      },
      {
        type: 'spring',
        mass: 1,
        stiffness: 80,
        damping: 20
      }
    );

  const hoverEndAnimation = (element: Element) =>
    animate(
      element,
      {
        scale: 1,
        boxShadow: '0 0 32px 0 rgba(65, 91, 255, 0.6)'
      },
      {
        type: 'spring',
        mass: 1,
        stiffness: 80,
        damping: 20
      }
    );

  useEffect(() => {
    if (!buttonRef.current)
      return () => {
        //
      };

    const cancelHover = hover(buttonRef.current, (element) => {
      const hoverStart = hoverStartAnimation(element);

      return () => {
        hoverStart.stop();
        hoverEndAnimation(element);
      };
    });

    return () => {
      cancelHover();
    };
  }, []);

  return (
    <button css={buttonStyle} type="button" {...props} ref={buttonRef}>
      <Icon icon="special_solid" size="32px" color={color.white} css={textStyle} />
      <span css={textStyle}>{children}</span>
    </button>
  );
}

const buttonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 24px;
  position: relative;
  border-radius: 12px;
  color: ${color.white};
  background: linear-gradient(94deg, #4a41ff 0%, #22f8e2 100%);
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  border: none;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: 0 0 32px 0 rgba(65, 91, 255, 0.6);
`;

const textStyle = css`
  font-feature-settings: 'liga' off, 'clig' off;
  text-shadow: 0 0 4px rgba(249, 240, 255, 0.4);
`;
